import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [isListening, setIsListening] = useState(false);
  const [audioData, setAudioData] = useState(new Uint8Array(128)); // Reduced size
  const [frequencyData, setFrequencyData] = useState(new Uint8Array(128)); // Reduced size
  const [audioSource, setAudioSource] = useState('microphone'); // 'microphone' or 'music'

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const musicSourceRef = useRef(null);
  const animationRef = useRef(null);

  const startMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      
      // Optimized settings for better performance
      analyserRef.current.fftSize = 256; // Reduced from 512
      analyserRef.current.smoothingTimeConstant = 0.7; // Reduced smoothing
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      microphoneRef.current = stream;
      setAudioSource('microphone');
      setIsListening(true);
      
      startAudioProcessing();
      
      return { success: true };
    } catch (error) {
      console.error('Error accessing microphone:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const connectMusicSource = useCallback((audioElement) => {
    if (!audioElement) {
      stopMicrophone();
      return;
    }

    try {
      // Stop microphone if it's running
      if (microphoneRef.current) {
        microphoneRef.current.getTracks().forEach(track => track.stop());
        microphoneRef.current = null;
      }

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      
      // Optimized settings
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.7;
      
      const source = audioContextRef.current.createMediaElementSource(audioElement);
      source.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);
      
      musicSourceRef.current = source;
      setAudioSource('music');
      setIsListening(true);
      
      startAudioProcessing();
    } catch (error) {
      console.error('Error connecting music source:', error);
    }
  }, []);

  const startAudioProcessing = useCallback(() => {
    const updateAudioData = () => {
      if (analyserRef.current) {
        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        const frequencyArray = new Uint8Array(bufferLength);
        
        analyserRef.current.getByteTimeDomainData(dataArray);
        analyserRef.current.getByteFrequencyData(frequencyArray);
        
        // Throttle updates for better performance (every other frame)
        if (animationRef.current % 2 === 0) {
          setAudioData(dataArray);
          setFrequencyData(frequencyArray);
        }
      }
      
      animationRef.current = requestAnimationFrame(updateAudioData);
    };
    
    updateAudioData();
  }, []);

  const stopMicrophone = useCallback(() => {
    if (microphoneRef.current) {
      microphoneRef.current.getTracks().forEach(track => track.stop());
      microphoneRef.current = null;
    }
    
    if (musicSourceRef.current) {
      musicSourceRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    analyserRef.current = null;
    setIsListening(false);
    setAudioData(new Uint8Array(128));
    setFrequencyData(new Uint8Array(128));
  }, []);

  const value = {
    isListening,
    audioData,
    frequencyData,
    audioSource,
    startMicrophone,
    stopMicrophone,
    connectMusicSource
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};